import { Loader2, Store } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { MoXat } from '@/components/help-center/latest-power-shared';
import { PainelDatePickerField } from '@/components/painel/cms/PainelDatePickerField';
import { RichTextEditor } from '@/components/painel/cms/RichTextEditor';
import { painelInputClass } from '@/components/painel/cms/painel-form';
import {
  painelSelectContentClass,
  painelSwitchClass,
} from '@/components/painel/painel-styles';
import { API_CATEGORY_DEFS } from '@/lib/powers-data';
import type { PowerEditorInput } from '@/lib/powers-cms';
import { cn } from '@/lib/utils';

function ReadOnlyField({
  label,
  hint,
  value,
  loading,
  showXatsIcon,
}: {
  label: string;
  hint?: string;
  value: string;
  loading?: boolean;
  showXatsIcon?: boolean;
}) {
  const showIcon = Boolean(showXatsIcon && value && value !== '—' && !loading);

  return (
    <div className="space-y-2">
      <Label className="font-poppins text-slate-300">{label}</Label>
      <div
        className={cn(
          painelInputClass,
          'flex min-h-[42px] cursor-not-allowed select-none items-center gap-1.5 bg-white/[0.02] font-inter text-sm text-slate-300 opacity-95'
        )}
        aria-readonly
      >
        {loading ? (
          <span className="inline-flex items-center gap-2 text-slate-500">
            <Loader2 className="h-4 w-4 animate-spin" />
            A consultar API…
          </span>
        ) : (
          <>
            {showIcon ? <MoXat className="h-4 w-4" /> : null}
            <span>{value}</span>
          </>
        )}
      </div>
      {hint ? <p className="font-inter text-xs text-slate-500">{hint}</p> : null}
    </div>
  );
}

export type PowerEditorBasicInfoFieldsProps = {
  form: PowerEditorInput;
  mode: 'new' | 'edit';
  slugConflict: boolean;
  slugTouched: boolean;
  onSlugTouched: () => void;
  onPatch: (partial: Partial<PowerEditorInput>) => void;
  editorResetKey: string;
  selectTriggerClass: string;
  storePriceLabel: string;
  fairtradeMinLabel: string;
  fairtradeMaxLabel: string;
  fairtradeRangeLabel: string;
  fairtradeUpdatedAt: string | null;
  apiLoading: boolean;
  fairtradeLoading: boolean;
  slugKey: string;
  hasStorePrice: boolean;
  hasResolvedFairtrade: boolean;
};

export function PowerEditorBasicInfoFields({
  form,
  slugConflict,
  onSlugTouched,
  onPatch,
  editorResetKey,
  selectTriggerClass,
  storePriceLabel,
  fairtradeMinLabel,
  fairtradeMaxLabel,
  fairtradeRangeLabel,
  fairtradeUpdatedAt,
  apiLoading,
  fairtradeLoading,
  slugKey,
  hasStorePrice,
  hasResolvedFairtrade,
}: PowerEditorBasicInfoFieldsProps) {
  return (
    <div className="space-y-5">
      <div className="grid gap-5 md:grid-cols-2">
        <div className="space-y-2">
          <Label className="font-poppins text-slate-300">Nome</Label>
          <Input
            value={form.name}
            onChange={(e) => onPatch({ name: e.target.value })}
            className={painelInputClass}
            placeholder="Ex.: Maus"
          />
        </div>
        <div className="space-y-2">
          <Label className="font-poppins text-slate-300">Slug (URL)</Label>
          <Input
            value={form.slug}
            onChange={(e) => {
              onSlugTouched();
              onPatch({ slug: e.target.value.toLowerCase() });
            }}
            className={cn(painelInputClass, 'font-mono')}
            placeholder="maus"
          />
          {slugConflict ? (
            <p className="text-xs text-rose-300">Já existe um power com este slug.</p>
          ) : null}
        </div>
        <div className="space-y-2">
          <Label className="font-poppins text-slate-300">ID xat</Label>
          <Input
            type="number"
            value={form.id || ''}
            onChange={(e) => onPatch({ id: Number(e.target.value) || 0 })}
            className={painelInputClass}
          />
        </div>
        <div className="space-y-2">
          <Label className="font-poppins text-slate-300">Categoria</Label>
          <Select value={form.category} onValueChange={(v) => onPatch({ category: v })}>
            <SelectTrigger className={selectTriggerClass}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent className={painelSelectContentClass}>
              {API_CATEGORY_DEFS.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <PainelDatePickerField
          id="power-released"
          label="Data de lançamento"
          value={form.released}
          onChange={(released) => onPatch({ released })}
        />
        <div className="flex items-end gap-3 pb-1">
          <Switch
            id="limited-modal"
            checked={form.limited}
            onCheckedChange={(v) => onPatch({ limited: v })}
            className={painelSwitchClass}
          />
          <Label htmlFor="limited-modal" className="font-poppins text-slate-300">
            Power limitado
          </Label>
        </div>
      </div>

      <div className="rounded-xl border border-white/10 bg-white/[0.02] p-4 md:p-5">
        <div className="mb-4 flex items-center gap-2">
          <Store className="h-4 w-4 text-emerald-300/80" aria-hidden />
          <h3 className="font-poppins text-sm font-semibold text-white">Preços</h3>
        </div>
        <div className="grid gap-5 md:grid-cols-3">
          <ReadOnlyField
            label="Preço na loja (xats)"
            value={storePriceLabel}
            loading={apiLoading && Boolean(slugKey)}
            showXatsIcon={hasStorePrice && !apiLoading}
            hint="Somente leitura — sincronizado pela API xatBlog."
          />
          <ReadOnlyField
            label="Fairtrade mín."
            value={fairtradeMinLabel}
            loading={fairtradeLoading && Boolean(slugKey)}
            showXatsIcon={hasResolvedFairtrade}
            hint="Somente leitura — sincronizado pela API FairTrade."
          />
          <ReadOnlyField
            label="Fairtrade máx."
            value={fairtradeMaxLabel}
            loading={fairtradeLoading && Boolean(slugKey)}
            showXatsIcon={hasResolvedFairtrade}
            hint={
              fairtradeUpdatedAt
                ? `Faixa atual: ${fairtradeRangeLabel || '—'} · API ${new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(fairtradeUpdatedAt))}`
                : 'Atualiza automaticamente quando a API FairTrade é consultada.'
            }
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label className="font-poppins text-slate-300">Descrição</Label>
        <RichTextEditor
          value={form.descriptionShort}
          onChange={(descriptionShort) => onPatch({ descriptionShort })}
          toolbar="extended"
          editorMinHeightClass="min-h-[120px]"
          resetKey={`${editorResetKey}-short-modal`}
        />
        <p className="font-inter text-xs text-slate-500">
          Aparece no hero da página pública do power.
        </p>
      </div>
    </div>
  );
}
